import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild, } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, filter, switchMap, take, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

import { IProject } from '../../../shared/models/project.model';
import { ProjectManagementService as ProjectManagementService } from '../../../home/services/project-management.service';
import { PhoneService } from '../../../shared/services/phone.service';
import { PHONE_REGEX } from '../../../shared/constants/phone-regex.constants';
import {
  PROJECT_NAME_INPUT_TIMEOUT
} from '../../../shared/constants/project-form.constants';
import { editErrorTextMessage } from '../../../project-layer/helpers/project-layer.helper';
import { UtilService } from '../../../shared/services/util.service';
import { formatDateToString } from '../../helpers/datepicker.helper';
import { UserService } from '../../../shared/services/user.service';
import { DataLayerService } from '../../../shared/services/data-layer.service';

// eslint-disable-next-line @typescript-eslint/no-explicit-any, no-var
declare var jQuery: any;

@Component({
    selector: 'app-ui-project-create',
    templateUrl: './project-create.component.html',
    styleUrls: ['./project-create.component.scss'],
    standalone: false
})
export class ProjectCreateComponent implements OnInit, OnDestroy {
  private projectClickSubscription: Subscription;
  private nexProjectSubscription: Subscription;

  public registerForm: FormGroup;
  public currentProject: IProject;
  public isNewProject = true;
  public date: Date;
  public projectNameExists = false;
  public multipleCities = [];
  public userIsOwner = false;

  @Output() projectUpdated = new EventEmitter<IProject>();
  @ViewChild('projectCreateModal') projectCreateModalEl: ElementRef;

  private projectNameSubject$ = new Subject();
  private projectNameSubscription$: Subscription;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly projectManagementService: ProjectManagementService,
    private readonly phoneService: PhoneService,
    private readonly router: Router,
    private readonly utilService: UtilService,
    private readonly userService: UserService,
    private readonly dataLayerService: DataLayerService
  ) {
  }

  ngOnInit() {
    this.buildBasicForm();

    this.projectNameSubscription$ = this.projectNameSubject$.pipe(
      debounceTime(PROJECT_NAME_INPUT_TIMEOUT),
      filter((projectName: string) => !!projectName),
      switchMap((projectName: string) => {
        return this.projectManagementService.checkIfProjectNameExists(projectName);
      })
    ).subscribe((data: boolean) => {
      this.projectNameExists = data;
    });
  }

  openModal(project: IProject | null) {
    jQuery(this.projectCreateModalEl.nativeElement).modal('show');
    this.clearMultipleCities();
    this.projectNameExists = false;

    if (project) {
      this.checkIfUserIsProjectOwner(project);
      this.loadProjectValues(project);
    } else {
      this.userIsOwner = true;
      this.patchDefaultValues();
    }
  }

  private checkIfUserIsProjectOwner(project: IProject) {
    this.userService.getUserInformation()
      .pipe(
        take(1),
        tap(user => {
          if (user?.id === project.projectOwner.id) {
            this.userIsOwner = true;
          }
        })
      )
      .subscribe()
  }

  private loadProjectValues(project: IProject) {
    this.currentProject = project;
    this.isNewProject = this.currentProject.isNewProject;

    this.registerForm.setValue({
      email: this.currentProject.email,
      projectName: this.currentProject.projectName,
      projectDescription: this.currentProject.projectDescription,
      street: this.currentProject.street,
      clientFirstName: this.currentProject.clientFirstName,
      contactPerson: this.currentProject?.contactPerson,
      clientPostalCode: this.currentProject.clientPostalCode,
      clientCity: this.currentProject.clientCity,
      phoneNumber: this.currentProject.phoneNumber,
      countryCode: this.currentProject.countryCode,
      remarks: this.currentProject.remarks,
      lastModified: this.currentProject.lastModified,
      created: this.currentProject.created,
      installationDate: this.currentProject.installationDate ? new Date(this.currentProject.installationDate) : null,
      shareWithinCompany: this.currentProject.shareWithinCompany
    });
  }

  /**
   * @deprecated
   */
  private populateCityAndPostalInputs(formatted: { city?: string, postal?: string }) {
    if (formatted?.city || formatted?.postal) {
      this.registerForm.get('clientPostalCode').setValue(formatted?.postal);
      this.registerForm.get('clientCity').setValue(formatted?.city);
    }
  }

  private extractFromAddressComponent(address, component: string[]) {
    return address?.address_components.find(a => {
      return JSON.stringify(a.types) === JSON.stringify(component);
    });
  }

  choseCity(city: string) {
    this.clearMultipleCities();
    this.registerForm.get('clientCity').setValue(city);
  }

  private clearMultipleCities() {
    this.multipleCities = [];
  }

  buildBasicForm() {
    // eslint-disable-next-line no-useless-escape, max-len
    const regEmail = /^(?!.*\.{2})([a-zA-Z0-9\u00c4-\u00e4\u00d6-\u00f6\u00dc-\u00fc\u00df!#$%&'*+\/=?^_`{|}~-]+([\.][a-zA-Z0-9\u00c4-\u00e4\u00d6-\u00f6\u00dc-\u00fc\u00df!#$%&'*+\/=?^_`{|}~-]+)*)@((([\-]?[a-zA-Z0-9\u00c4-\u00e4\u00d6-\u00f6\u00dc-\u00fc\u00df]){2,}[\.])*(([a-zA-Z0-9\u00c4-\u00e4\u00d6-\u00f6\u00dc-\u00fc\u00df][\-]?))+).(([\.]([a-zA-Z0-9\u00c4-\u00e4\u00d6-\u00f6\u00dc-\u00fc\u00df][\-]?){2,}([a-zA-Z0-9\u00c4-\u00e4\u00d6-\u00f6\u00dc-\u00fc\u00df])*)+)$/;

    this.registerForm = this.formBuilder.group({
      email: ['', [Validators.maxLength(255), Validators.pattern(regEmail)]],
      projectName: ['', [Validators.maxLength(76)]],
      projectDescription: ['', [Validators.maxLength(100)]],
      street: ['', [Validators.maxLength(50)]],
      remarks: ['', [Validators.maxLength(200)]],
      clientFirstName: ['', [Validators.maxLength(25)]],
      contactPerson: ['', [Validators.maxLength(100)]],
      clientPostalCode: ['', [Validators.maxLength(25)]],
      clientCity: ['', [Validators.maxLength(75)]],
      phoneNumber: ['', [Validators.maxLength(20), Validators.pattern(PHONE_REGEX)]],
      countryCode: ['', [Validators.maxLength(3)]],
      created: [''],
      lastModified: [''],
      installationDate: [''],
      shareWithinCompany: [false]
    });
  }

  async patchDefaultValues() {
    this.registerForm.reset();
    this.registerForm.controls.shareWithinCompany.setValue(false);

    this.currentProject = {} as IProject;
    this.isNewProject = true;
    this.date = new Date();
  }

  private getSubmitProjectFromForm(formValue: IProject): IProject {
    if (formValue?.installationDate) {
      return {
        ...formValue,
        // Because the JSON.stringify(formValue.installationDate) function saves installationDate in
        // UTC (Coordinated Universal Time) timezone,
        // the correct installationDate could be changed to different date in UTC timezone.
        // That's why installationDate will be sent as a string
        installationDate: formatDateToString(formValue?.installationDate as Date)
      }
    }

    return formValue;
  }

  async onSubmit(formValue: IProject) {
    try {
      if (!this.registerForm.valid) {
        return;
      }
      formValue.projectName = formValue.projectName.trim();
      const project = await this.projectManagementService.createNewProject(this.getSubmitProjectFromForm(formValue));

      const navigationLink = `project/${project.projectId}/overview`;
      this.dataLayerService.pushCtaOnProjectActionTrigger(`${location.origin}/${ navigationLink }`, 'Projects list', 'Create project');
      this.router.navigate([navigationLink]);

    } catch ({ error }) {
      const [ e ] = error?.errors ?? [];
      editErrorTextMessage(e);
      this.utilService.showToastWarning(error);
    }
  }

  async onUpdate(formValue: IProject) {
    try {
      if (!this.registerForm.valid) {
        return;
      }

      formValue.lastModified = new Date();
      formValue.projectId = this.currentProject.projectId;
      const project = await this.projectManagementService.updateProject(this.getSubmitProjectFromForm(formValue));

      this.projectUpdated.emit(project);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      console.log('Cannot update project.');
    }
  }

  updateAddressInformation(inputField: string) {
    const search = [];

    const street = this.registerForm.get('street').value;
    const city = this.registerForm.get('clientCity').value;
    const clientPostalCode = this.registerForm.get('clientPostalCode').value;

    if (inputField === 'clientPostalCode') {
      if (street) {
        search.push(street);
      }

      search.push(clientPostalCode);
    }

    if (inputField === 'clientCity') {
      if (street) {
        search.push(street);
      }

      search.push(city);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  async updateProjectName() {
  }

  keyPressBlocker(event: KeyboardEvent) {
    if (event.code === 'Space') {
      event.preventDefault();
    }
  }

  checkIfProjectNameDuplicate(projectName: string) {
    if (this.currentProject.projectName !== projectName) {
      this.projectNameSubject$.next(projectName.trim());
    } else {
      this.projectNameExists = false;
    }
  }

  regeneratePhoneNumber(phoneNumber) {
    const updatedPhoneNumber = this.phoneService.generatePhoneNumber(phoneNumber, this.registerForm.get('phoneNumber').valid);
    this.registerForm.get('phoneNumber').setValue(updatedPhoneNumber);
  }

  changeInstallationDate(date: Date) {
    this.registerForm.get('installationDate').setValue(date);
  }

  resetInstallationDate() {
    this.registerForm.get('installationDate').setValue(null);
  }

  toggleShareWithingCompany(isChecked: boolean) {
    this.registerForm.controls.shareWithinCompany.setValue(!isChecked);
  }

  ngOnDestroy() {
    this.projectClickSubscription?.unsubscribe();
    this.nexProjectSubscription?.unsubscribe();
    this.projectNameSubscription$?.unsubscribe();
  }

  closeModal() {
    (document.activeElement as HTMLElement)?.blur();
    jQuery(this.projectCreateModalEl.nativeElement).modal('hide');
  }
}
